/**
 * Portfolio API Service
 * Handles all portfolio-related API calls to the backend
 * Maps directly to portfolio-service endpoints through the gateway
 * TASK-086: Updated to use httpClient for authentication
 * TASK-263: Updated to support multi-portfolio per client
 */

import { httpClient, httpClientLongRunning } from '../../lib/httpClient';

// API endpoint constants for Pretim integration
const PRETIM_ENDPOINTS = {
  SYNC: '/api/portfolio/external/pretim/sync',
  STATUS: '/api/portfolio/external/pretim/status',
  SYNC_HISTORY: (portfolioId: string) => `/api/portfolio/external/pretim/sync-history/${portfolioId}`,
  PORTFOLIO: (portfolioId: string) => `/api/portfolio/external/pretim/portfolio/${portfolioId}`,
  PORTFOLIO_CONFIG: (portfolioId: string) => `/api/portfolio/external/pretim/portfolio/${portfolioId}/config`,
} as const;

// Types for API responses
export interface PortfolioOverview {
  client_id: string;
  client_name?: string;  // TASK-277: Added client name
  total_value: number;
  total_cash: number;
  total_securities: number;
  health_score: number;
  allocation_by_asset_class: string | Record<string, number>;
  allocation_by_currency: string | Record<string, number>;
  total_unrealized_pl: number;
  sharpe_ratio?: number;
  volatility?: number;
  var_1day?: number;
  cache_valid_until?: string;
  realized_income_ytd?: number;
  unrealized_gains?: number;
  dividend_yield?: number;
}

export interface Position {
  id: string;
  client_id: string;
  portfolio_id?: string; // TASK-263: Multi-portfolio support
  isin: string;
  security_name: string;
  quantity: number;
  market_value: number;
  // TASK-865: server-derived CHF value (shared/portfolio/fx.py). null = no rate for the
  // position's currency. `market_value` stays the custodian's local figure.
  market_value_base?: number | null;
  currency: string;
  market_price: number;
  asset_class: string;
  book_value: number;
  unrealized_pl: number;
  current_price?: number;
  current_value?: number;
  price_change?: number;
  sector?: string;
  expected_dividend_date?: string;
  // Position metadata (Pretim v1.1)
  position_date?: string;
  security_type?: string;
  price_factor?: number;
  // Local currency values (multi-currency display)
  market_value_local?: number;
  market_value_local_ccy?: string;
  cost_value_local?: number;
  cost_value_local_ccy?: string;
  // Accrued interest (bond positions)
  accrued_interest_base?: number;
  accrued_interest_base_ccy?: string;
  accrued_interest_local?: number;
  accrued_interest_local_ccy?: string;
  // TWR extended
  twr_ytd?: number;
  twr_today?: number;
  twr_today_factor?: number;
  twr_ytd_factor?: number;
  twr_starting_base_mv?: number;
  twr_ending_base_mv?: number;
  // FX rate (local to base)
  fx_rate_local_to_base?: number;
  fx_local_currency?: string;
  fx_base_currency?: string;
  // Classification hierarchy
  country?: string;
  region?: string;
  industry_group?: string;
  industry?: string;
  sub_industry?: string;
  sub_asset_class?: string;
  // Security identifiers (ISIN, CUSIP, SEDOL, Bloomberg, etc.)
  security_identifiers?: Record<string, string>;
  // Cash account reference
  cash_account_name?: string;
  cash_account_number?: string;
  // Market identifier code (exchange)
  market_identifier_code?: string;
}

/**
 * TASK-868: a sized rebalancing proposal for one breached slot.
 *
 * Every row names a position the client actually holds in that slot — the server picks them
 * with the same classification rules the drift engine used to raise the breach, so a proposal
 * can never reference a holding the book does not contain (TASK-870).
 */
export interface RebalanceProposalRow {
  position_id: string;
  isin: string | null;
  security_name: string | null;
  slot: string;
  currency: string | null;
  current_value_chf: number;
  trim_chf: number;
  post_value_chf: number;
  /** False on this book: every position carries `quantity: 1` with `market_price == market_value`. */
  share_sizing_available: boolean;
}

export interface RebalanceSlotVerdict {
  by_slot: Record<string, {
    current: number;
    target: number;
    drift: number;
    breach: boolean;
    asset_class: string | null;
  }>;
  max_drift_pct: number | null;
  total_portfolio_value: number | null;
}

export interface RebalanceProposal {
  client_id: string;
  portfolio_id: string | null;
  slot: string;
  /** Always 'CHF' today — see `share_sizing_available`. TASK-868 criterion 6. */
  sizing_unit: string;
  share_sizing_available: boolean;
  trim_chf: number;
  /** What the slot needed but its holdings could not give. Non-zero is a real condition. */
  shortfall_chf: number;
  slot_value_chf: number;
  total_portfolio_value: number;
  rows: RebalanceProposalRow[];
  drift_before: RebalanceSlotVerdict;
  /** COMPUTED by re-running the real drift calculation over the trimmed book, never narrated. */
  drift_after: RebalanceSlotVerdict;
  breaches_before: string[];
  breaches_after: string[];
  new_breaches: string[];
  /** Was the slot actually in breach before this proposal? An overweight slot inside its band
   *  can still be trimmed, and a surface must not imply a breach that was not there. */
  slot_was_breached: boolean;
  resolves_target_slot: boolean;
  /**
   * The flag no surface may ignore. A proposal that fixes the slot it was asked about and
   * breaches another has NOT succeeded — on Schneider's book, trimming Foreign (Dev. Markets)
   * into cash always breaches Liquidity, and the assistant asked to simulate it reported
   * success anyway (TASK-872).
   */
  opens_new_breach: boolean;
  calculation_version: number | null;
  tolerance_pct: number | null;
  timestamp: string;
}

/** A row of `market_data.portfolio_proposals` as the list endpoint returns it. */
export interface SavedProposal {
  id: string;
  client_id: string;
  proposal_name: string;
  proposal_type: string;
  status: string;
  current_portfolio: unknown;
  proposed_portfolio: unknown;
  transactions: RebalanceProposalRow[] | null;
  current_metrics: RebalanceSlotVerdict | null;
  proposed_metrics: RebalanceSlotVerdict | null;
  rationale: string | null;
  created_at: string;
}

/**
 * TASK-875: the one drift figure a surface is allowed to show, with everything needed to read
 * it.
 *
 * The payload carries TWO numbers and neither said what it was. `drift_percentage` is a
 * whole-portfolio dispersion measure — the sum of |drift| over all 12 slots, halved — and the
 * ±tolerance band is not measured against it. Two surfaces filtered on `breached_slots` and
 * displayed `drift_percentage`, so Marius Huber was correctly excluded from the rebalance list
 * on a figure (2.24) that reads as over a 2.0 tolerance while his largest slot deviation was
 * 1.67.
 *
 * Render `headline`. It is an object precisely so a surface cannot pick the wrong field.
 */
export interface DriftHeadline {
  /** The raw payload field this came from, for anyone tracing it back. */
  metric: 'max_drift_pct';
  /** What the number is, in words a surface can print beside it. */
  label: string;
  value: number;
  unit: 'pp';
  tolerance_pct: number;
  in_band: boolean;
  /** The slots the filter used, with their own Δpp — so a breach list can show what it
   *  filtered on rather than an unrelated aggregate. */
  breaches: Array<{
    slot: string;
    drift_pp: number | null;
    current: number | null;
    target: number | null;
  }>;
}

export interface AssetAllocation {
  by_asset_class: Record<string, number>;
  by_currency: Record<string, number>;
  by_sector: Record<string, number>;
  by_geography?: Record<string, number>;
  // TASK-874: what kind of place each by_geography key is. A `region` bucket holds only
  // positions with no country-level classification, so it never contains the countries listed
  // beside it. `unresolved` is a label the backend vocabulary could not place — it may well be
  // a country, so nothing may describe it as a region.
  by_geography_tier?: Record<string, 'country' | 'region' | 'unresolved' | 'unknown'>;
  total_value: number;
  targets?: Record<string, number> | Array<{
    asset_class: string;
    target_weight: number;
    current_weight: number;
  }>;
  rebalance_needed?: boolean;
}

export interface CashPosition {
  id: string;
  portfolio_id?: string;
  currency: string;
  amount: number;
  account_type: string;
  // TASK-865: server-derived CHF value (shared/portfolio/fx.py). null = no rate.
  amount_base?: number | null;
  base_currency?: string;
  // Pretim v1.1 cash account metadata
  account_name?: string;
  account_number?: string;
  country_of_incorporation?: string;
  region?: string;
}

export interface PerformanceMetrics {
  period: string;
  total_return: number | null;
  annualized_return: number | null;
  volatility: number | null;
  sharpe_ratio: number | null;
  max_drawdown: number | null;
  win_rate: number | null;
  information_ratio?: number | null;
  tracking_error?: number | null;
  active_share?: number | null;
  period_return?: number | null;
  today_return?: number | null;
  week_return?: number | null;
  ytd_return?: number | null;
  month_return?: number | null;
  quarter_return?: number | null;
  one_year_return?: number | null;
  three_year_return?: number | null;
  five_year_return?: number | null;
  since_inception_return?: number | null;
  current_drawdown?: number | null;
  benchmark_return?: number | null;
  benchmark_name?: string | null;
  benchmark_code?: string;
  alpha?: number | null;
  beta?: number | null;
  sortino_ratio?: number | null;
  calmar_ratio?: number | null;
  mtd_return?: number | null;
  qtd_return?: number | null;
  // TWR extended (Pretim v1.1 portfolio-level)
  twr_ytd?: number;
  twr_ytd_factor?: number;
  twr_starting_base_mv?: number;
  twr_ending_base_mv?: number;
  fees?: {
    management_fee?: number;
    performance_fee?: number;
    total_fees?: number;
    fee_impact?: number;
  };
  attribution?: PerformanceAttribution;
  performance_table?: Array<{
    metric: string;
    value: number;
    benchmark?: number;
    difference?: number;
  }>;
}

// TASK-582 honest-scope: history-derived metrics are null (not 0) when there isn't enough
// real history to compute them; data_quality says why. Concentration is always real.
export interface RiskDataQuality {
  returns_count: number;
  min_required: number;
  sufficient: boolean;
  source: 'history' | 'synthetic' | 'insufficient' | 'error';
  benchmark_used: string | null;
  note?: string;
}

export interface RiskMetrics {
  volatility: number | null;
  var_1day: number | null;
  var_10day: number | null;
  var_95?: number | null;
  beta: number | null;
  concentration_risk: number;
  sharpe_ratio?: number | null;
  expected_shortfall?: number | null;
  data_quality?: RiskDataQuality;
}

export interface StressTests {
  [scenario: string]: number;
}

export interface Transaction {
  id: string;
  trade_date: string;
  isin: string;
  security_name: string;
  transaction_type: string;
  quantity: number;
  price: number;
  gross_amount: number;
  fees: number;
  net_amount: number;
  currency: string;
  settlement_date: string;
  description?: string;
  // Pretim v1.1 extended fields
  parent_id?: string;
  transaction_type_group?: string;
  is_cancelled?: boolean;
  price_currency?: string;
  exchange_rate?: number;
  comment?: string;
  // Accrued interest (bond transactions)
  accrued_interest_local?: number;
  accrued_interest_local_ccy?: string;
  // FX rates (full chain: local → settlement → base)
  fx_local_to_settlement_rate?: number;
  fx_settlement_to_base_rate?: number;
  // Settlement cash account
  settlement_cash_account_name?: string;
  settlement_cash_account_number?: string;
  // Realized result (P&L from completed trades)
  realized_result_local?: number;
  realized_result_local_ccy?: string;
  realized_result_base_price?: number;
  realized_result_base_price_ccy?: string;
  realized_result_base_currency?: number;
  realized_result_base_currency_ccy?: string;
}

export interface PerformanceAttribution {
  asset_allocation: number;
  security_selection: number;
  currency: number;
  other: number;
  total: number;
}

export interface RollingReturns {
  date: string;
  returns: number;
  benchmark_returns?: number;
}

export interface CorrelationMatrix {
  assets: string[];
  matrix: number[][];
}

export interface VarDistribution {
  percentile: number;
  loss: number;
  probability?: number;
}

export interface ClientPortfolio {
  id?: string; // Portfolio ID (optional for backward compatibility)
  portfolio_id?: string; // Alternative portfolio ID field
  name?: string; // Portfolio name
  portfolio_name?: string; // Alternative portfolio name field
  client_id: string;
  client_name: string;
  risk_profile: string;
  total_value: number | null;
  health_score: number | null;
  performance: number | null;
  updated_at: string | null;
  /**
   * TASK-723 (review 2026-07-29): 'unknown' is a real state — the summary has no health score
   * for the client and does not assess allocation drift, so it has nothing to base a verdict
   * on. 'rebalance' is retained because the shape is the contract, but this endpoint cannot
   * currently produce it; `drift_assessed` says so explicitly rather than leaving the reader
   * to infer that a zero count means zero clients.
   */
  status: 'healthy' | 'attention' | 'rebalance' | 'unknown';
  /** False on this endpoint: it computes no drift. `/my-book` is the drift-aware summary. */
  drift_assessed?: boolean;
}

/**
 * Portfolio entity for multi-portfolio support (TASK-263)
 * TASK-321: Added sync fields for Pretim integration
 */
export interface Portfolio {
  id: string;
  account_id: string;
  client_id: string;
  name: string;
  portfolio_type: 'investment' | 'retirement' | 'trust' | 'savings' | 'other';
  custodian?: string;
  custodian_account_number?: string;
  currency: string;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  created_by_user_id?: string;
  // TASK-321: Sync configuration fields
  data_source?: 'manual' | 'pretim' | 'csv_import';
  external_portfolio_id?: string;
  external_id?: string;  // Alias for external_portfolio_id
  sync_enabled?: boolean;
  sync_frequency?: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
  last_sync_at?: string;
  last_sync_status?: 'success' | 'partial' | 'failed' | 'pending';
  // Pretim v1.1 portfolio metadata
  portfolio_number?: string;
  wealth_manager_id?: string;
  wealth_manager_name?: string;
  // TASK-657: server-derived source/capability contract — the ONE truth for
  // ownership mode, connection state, and permitted actions.
  capability_contract?: PortfolioCapabilityContract;
}

/** TASK-657: capability contract attached by the portfolio service. */
export interface PortfolioCapabilityContract {
  mode: 'connected' | 'kielis_managed';
  source: string;
  source_label: string;
  custodian?: string | null;
  connection_status:
    | 'not_applicable'
    | 'never_synced'
    | 'success'
    | 'partial'
    | 'failed'
    | 'pending'
    | 'running';
  last_sync_at?: string | null;
  capabilities: {
    holdings_read: boolean;
    transactions_read: boolean;
    performance_read: boolean;
    portfolio_write: boolean;
    proposal_export: boolean;
    proposal_push: boolean;
    orders_write: boolean;
    cash_reconciliation: boolean;
    valuation_calculation: boolean;
  };
}

/**
 * Create portfolio request (TASK-263)
 */
export interface CreatePortfolioRequest {
  name: string;
  portfolio_type?: 'investment' | 'retirement' | 'trust' | 'savings' | 'other';
  custodian?: string;
  currency?: string;
  is_default?: boolean;
  // TASK-321: Pretim integration fields
  external_portfolio_id?: string;
  data_source?: 'manual' | 'pretim' | 'csv_import' | string;
}

export interface PortfolioExtractionResult {
  status: 'success' | 'low_confidence' | 'no_data' | 'mapping_failed';
  confidence_score: number;
  extraction_method: string;
  detected_bank: string | null;
  detected_type: string | null;
  processing_time_seconds: number;
  positions_extracted: number;
  positions_mapped: number;
  database_records?: {
    inserted: number;
    updated: number;
    total: number;
  };
  metadata?: Record<string, any>;
  warnings?: string[];
  errors?: string[];
  message: string;
}

/**
 * TASK-321: Sync history entry for Pretim integration
 */
export interface SyncHistoryEntry {
  id: string;
  data_source: 'pretim' | 'csv_import' | 'manual';
  sync_type: 'full' | 'incremental' | 'positions_only' | 'transactions_only';
  external_portfolio_id?: string;
  status: 'pending' | 'running' | 'success' | 'partial' | 'failed';
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  positions_synced: number;
  positions_skipped: number;
  positions_failed: number;
  cash_synced: number;
  transactions_synced: number;
  error_message?: string;
  warnings?: string[];
}

/**
 * TASK-321: Sync history response
 */
export interface SyncHistoryResponse {
  portfolio_id: string;
  history: SyncHistoryEntry[];
  count: number;
  limit: number;
  offset: number;
  timestamp: string;
}

/**
 * TASK-321: Portfolio with sync configuration
 */
export interface PortfolioWithSyncInfo {
  id: string;
  client_id: string;
  name: string;
  portfolio_type: string;
  custodian?: string;
  currency: string;
  is_active: boolean;
  data_source: 'manual' | 'pretim' | 'csv_import';
  external_id?: string;
  sync_enabled: boolean;
  sync_frequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
  last_sync_at?: string;
  last_sync_status?: 'success' | 'partial' | 'failed' | 'pending';
  sync_error_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * TASK-321: Sync config update request
 */
export interface SyncConfigUpdate {
  data_source?: 'manual' | 'pretim' | 'csv_import';
  external_id?: string;
  sync_enabled?: boolean;
  sync_frequency?: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'manual';
}

/**
 * TASK-581: latest client-keyed portfolio analysis (PortfolioCommentary widget).
 * `analysis` is null while none exists; `status` is "generating" when the
 * backend kicked an auto-generation, "unavailable" when it recently failed.
 */
export interface PortfolioAnalysisFlag {
  status: string;
  detail?: string;
  items?: { name: string; weight_pct: number }[];
  conflicts?: string[];
  max_deviation_pp?: number;
  determined_profile?: string | null;
}

export interface PortfolioAnalysisLatest {
  client_id: string;
  status?: 'generating' | 'unavailable';
  analysis: {
    portfolio_analysis: string;
    confidence_score: number | null;
    flags: {
      suitability_drift?: PortfolioAnalysisFlag;
      esg_mismatch?: PortfolioAnalysisFlag;
      concentration?: PortfolioAnalysisFlag;
      snapshot?: Record<string, unknown>;
    } | null;
    delta: { changes: string[] } | null;
    language: string | null;
    generated_at: string | null;
  } | null;
}

// TASK-582 honest-scope: the persisted portfolio-risk read-out (RiskCommentary widget).
export interface RiskFlag {
  status: string;
  value?: number | null;
  worst?: { scenario: string; loss_pct: number } | null;
}

export interface PortfolioRiskLatest {
  client_id: string;
  status?: 'generating' | 'unavailable';
  analysis: {
    risk_analysis: string;
    confidence_score: number | null;
    flags: {
      volatility?: RiskFlag;
      value_at_risk?: RiskFlag;
      concentration?: RiskFlag;
      stress?: RiskFlag;
      data_quality?: RiskDataQuality | null;
      snapshot?: Record<string, unknown>;
    } | null;
    delta: { changes: string[] } | null;
    language: string | null;
    generated_at: string | null;
  } | null;
}

class PortfolioApiService {
  // TASK-086: No need for baseUrl, httpClient handles it

  /**
   * Build query string with optional portfolio_id (TASK-263)
   */
  private buildQueryParams(params: Record<string, string | number | undefined>): string {
    const filtered = Object.entries(params).filter(([, v]) => v !== undefined);
    if (filtered.length === 0) return '';
    return '?' + filtered.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
  }

  /**
   * Get portfolio overview with cached metrics
   * TASK-263: Added optional portfolioId parameter
   */
  async getPortfolioOverview(clientId: string, portfolioId?: string): Promise<PortfolioOverview> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    const data = await httpClient.get<PortfolioOverview>(`/api/portfolio/overview/${clientId}${query}`);

    // Parse JSON strings if needed
    if (typeof data.allocation_by_asset_class === 'string') {
      data.allocation_by_asset_class = JSON.parse(data.allocation_by_asset_class);
    }
    if (typeof data.allocation_by_currency === 'string') {
      data.allocation_by_currency = JSON.parse(data.allocation_by_currency);
    }

    return data;
  }

  /**
   * Get all portfolio positions for a client
   * TASK-263: Added optional portfolioId parameter
   */
  async getPositions(clientId: string, portfolioId?: string): Promise<{ positions: Position[]; count: number }> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.get<{ positions: Position[]; count: number }>(`/api/portfolio/positions/${clientId}${query}`);
  }

  /**
   * TASK-581: latest portfolio analysis for a client (auto-generates on miss).
   */
  async getLatestPortfolioAnalysis(clientId: string): Promise<PortfolioAnalysisLatest> {
    return httpClient.get<PortfolioAnalysisLatest>(`/api/portfolio-analysis/${clientId}/latest`);
  }

  // TASK-582 honest-scope: the persisted portfolio-risk read-out (RiskCommentary widget).
  async getLatestPortfolioRisk(clientId: string): Promise<PortfolioRiskLatest> {
    return httpClient.get<PortfolioRiskLatest>(`/api/portfolio-risk/${clientId}/latest`);
  }

  /**
   * TASK-479: apply a user-confirmed canonical sub_asset_class to one position.
   */
  async setPositionClassification(
    positionId: string,
    subAssetClass: string,
  ): Promise<{ position_id: string; sub_asset_class: string; updated: number }> {
    return httpClient.post<{ position_id: string; sub_asset_class: string; updated: number }>(
      `/api/portfolio/positions/${positionId}/classification`,
      { sub_asset_class: subAssetClass },
    );
  }

  /**
   * Get asset allocation breakdown
   * TASK-263: Added optional portfolioId parameter
   */
  async getAllocation(clientId: string, portfolioId?: string): Promise<{ allocation: AssetAllocation }> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.get<{ allocation: AssetAllocation }>(`/api/portfolio/allocation/${clientId}${query}`);
  }

  /**
   * TASK-868: build a sized rebalancing proposal for one breached slot.
   *
   * Builds, does not persist — call `savePortfolioProposal` to keep it. Opening a breached slot
   * to see what fixing it would cost should not itself create a record.
   */
  async buildRebalanceProposal(
    clientId: string,
    slot: string,
    portfolioId?: string,
  ): Promise<RebalanceProposal> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.post<RebalanceProposal>(
      `/api/portfolio/rebalance-proposal/${clientId}${query}`,
      { slot },
    );
  }

  /**
   * TASK-868: persist a built proposal through the route that has existed since migration 027
   * and that nothing had ever written to.
   */
  async savePortfolioProposal(
    clientId: string,
    proposal: RebalanceProposal,
    advisorId?: string,
  ): Promise<{ proposal_id: string; status: string }> {
    return httpClient.post<{ proposal_id: string; status: string }>(
      `/api/portfolio/proposals/${clientId}`,
      {
        name: `Rebalance ${proposal.slot}`,
        type: 'rebalancing',
        // Without this the server defaults `created_by` to "system"
        // (`main.py:save_portfolio_proposal`). This flow is the first and only writer to
        // `market_data.portfolio_proposals`, so every advice record it creates would be
        // unattributable to the advisor who built it — not a thing to discover later.
        advisor_id: advisorId,
        current_portfolio: proposal.drift_before.by_slot,
        proposed_portfolio: proposal.drift_after.by_slot,
        transactions: proposal.rows,
        current_metrics: proposal.drift_before,
        proposed_metrics: proposal.drift_after,
        rationale: proposal.opens_new_breach
          ? `Trims ${proposal.slot} to target; opens a new breach in `
            + `${proposal.new_breaches.join(', ')}.`
          : `Trims ${proposal.slot} to target with no new breach.`,
      },
    );
  }

  /** TASK-868: the saved proposals for a client, newest first. */
  async getPortfolioProposals(
    clientId: string,
    limit = 10,
  ): Promise<{ proposals: SavedProposal[]; count: number }> {
    const query = this.buildQueryParams({ limit });
    return httpClient.get<{ proposals: SavedProposal[]; count: number }>(
      `/api/portfolio/proposals/${clientId}${query}`,
    );
  }

  /**
   * Get cash positions by currency
   * TASK-263: Added optional portfolioId parameter
   */
  async getCashPositions(clientId: string, portfolioId?: string): Promise<{
    cash_positions: CashPosition[];
    total_cash_chf: number
  }> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.get<{ cash_positions: CashPosition[]; total_cash_chf: number }>(`/api/portfolio/cash/${clientId}${query}`);
  }

  /**
   * Get performance metrics for specified period
   * TASK-263: Added optional portfolioId parameter
   */
  async getPerformance(
    clientId: string,
    period: '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all' = '30d',
    portfolioId?: string,
    benchmark?: string
  ): Promise<{
    metrics: PerformanceMetrics;
    history: any[];
    top_contributors?: Array<{ name: string; contribution: number }>;
    bottom_contributors?: Array<{ name: string; contribution: number }>;
  }> {
    const query = this.buildQueryParams({ period, portfolio_id: portfolioId, benchmark });
    return httpClient.get<{
      metrics: PerformanceMetrics;
      history: any[];
      top_contributors?: Array<{ name: string; contribution: number }>;
      bottom_contributors?: Array<{ name: string; contribution: number }>;
    }>(`/api/portfolio/performance/${clientId}${query}`);
  }

  /**
   * Get risk metrics including VaR and stress tests
   * TASK-263: Added optional portfolioId parameter
   */
  async getRiskMetrics(clientId: string, portfolioId?: string): Promise<{
    risk_metrics: RiskMetrics;
    stress_tests: StressTests
  }> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.get<{ risk_metrics: RiskMetrics; stress_tests: StressTests }>(`/api/portfolio/risk/${clientId}${query}`);
  }

  /**
   * Get recent transactions with pagination
   * TASK-263: Added optional portfolioId parameter
   */
  async getTransactions(
    clientId: string,
    limit = 50,
    offset = 0,
    portfolioId?: string
  ): Promise<{
    transactions: Transaction[];
    count: number
  }> {
    const query = this.buildQueryParams({ limit, offset, portfolio_id: portfolioId });
    return httpClient.get<{ transactions: Transaction[]; count: number }>(`/api/portfolio/transactions/${clientId}${query}`);
  }

  /**
   * Get performance attribution breakdown
   * TASK-281: Returns null on error for graceful degradation
   */
  async getPerformanceAttribution(clientId: string): Promise<{
    attribution: PerformanceAttribution
  } | null> {
    try {
      return await httpClient.get<{ attribution: PerformanceAttribution }>(`/api/portfolio/performance/${clientId}/attribution`);
    } catch (error) {
      console.error('Error fetching performance attribution:', error);
      return null;
    }
  }

  /**
   * Get rolling returns history
   * TASK-281: Returns null on error for graceful degradation
   */
  async getRollingReturns(
    clientId: string,
    period: '3m' | '6m' | '12m' = '3m'
  ): Promise<{
    rolling_returns: RollingReturns[]
  } | null> {
    try {
      return await httpClient.get<{ rolling_returns: RollingReturns[] }>(`/api/portfolio/performance/${clientId}/rolling-returns?period=${period}`);
    } catch (error) {
      console.error('Error fetching rolling returns:', error);
      return null;
    }
  }

  /**
   * Get correlation matrix for portfolio assets
   * TASK-281: Returns null on error for graceful degradation
   */
  async getCorrelationMatrix(clientId: string): Promise<{
    correlation_matrix: CorrelationMatrix
  } | null> {
    try {
      return await httpClient.get<{ correlation_matrix: CorrelationMatrix }>(`/api/portfolio/risk/${clientId}/correlation-matrix`);
    } catch (error) {
      console.error('Error fetching correlation matrix:', error);
      return null;
    }
  }

  /**
   * Get VaR distribution data
   * TASK-281: Returns null on error for graceful degradation
   */
  async getVarDistribution(clientId: string): Promise<{
    var_distribution: VarDistribution[]
  } | null> {
    try {
      return await httpClient.get<{ var_distribution: VarDistribution[] }>(`/api/portfolio/risk/${clientId}/var-distribution`);
    } catch (error) {
      console.error('Error fetching VaR distribution:', error);
      return null;
    }
  }

  /**
   * Get portfolio summary for all clients (global view)
   */
  async getAllClientPortfolios(): Promise<{
    portfolios: ClientPortfolio[];
    count: number
  }> {
    return httpClient.get<{ portfolios: ClientPortfolio[]; count: number }>(`/api/portfolio/clients/portfolios`);
  }

  /**
   * Trigger full recalculation of portfolio metrics
   */
  async triggerRecalculation(clientId: string): Promise<{
    status: string;
    metrics: PortfolioOverview
  }> {
    return httpClient.post<{ status: string; metrics: PortfolioOverview }>(`/api/portfolio/calculate/${clientId}`);
  }

  /**
   * Helper method to get all data for a client dashboard
   * TASK-263: Added optional portfolioId parameter for multi-portfolio support
   * TASK-281: Core data fails hard, optional analytics fail gracefully
   */
  async getClientDashboardData(
    clientId: string,
    period: '7d' | '30d' | '90d' | '1y' | 'ytd' | 'all' = '30d',
    portfolioId?: string,
    benchmark?: string
  ) {
    // Core data - must succeed
    const [overview, positions, allocation, performance, risk, cash, transactions] = await Promise.all([
      this.getPortfolioOverview(clientId, portfolioId),
      this.getPositions(clientId, portfolioId),
      this.getAllocation(clientId, portfolioId),
      this.getPerformance(clientId, period, portfolioId, benchmark),
      this.getRiskMetrics(clientId, portfolioId),
      this.getCashPositions(clientId, portfolioId),
      this.getTransactions(clientId, 100, 0, portfolioId),
    ]);

    // Optional analytics - fail gracefully (endpoints may not exist yet)
    const [correlationResult, varResult, attributionResult] = await Promise.allSettled([
      this.getCorrelationMatrix(clientId),
      this.getVarDistribution(clientId),
      this.getPerformanceAttribution(clientId),
    ]);

    return {
      overview,
      positions: positions.positions,
      allocation: allocation.allocation,
      performance: performance.metrics,
      performanceHistory: performance.history,
      // TASK-387: Pass through backend-calculated contributors
      topContributors: performance.top_contributors ?? [],
      bottomContributors: performance.bottom_contributors ?? [],
      risk: risk.risk_metrics,
      stressTests: risk.stress_tests,
      cash: cash.cash_positions,
      totalCashCHF: cash.total_cash_chf,
      transactions: transactions.transactions,
      // Optional data - null if endpoint failed
      correlationMatrix: correlationResult.status === 'fulfilled' && correlationResult.value
        ? correlationResult.value.correlation_matrix
        : null,
      varDistribution: varResult.status === 'fulfilled' && varResult.value
        ? varResult.value.var_distribution
        : null,
      attribution: attributionResult.status === 'fulfilled' && attributionResult.value
        ? attributionResult.value.attribution
        : null,
    };
  }

  /**
   * Helper method to get global dashboard data
   */
  async getGlobalDashboardData() {
    try {
      const { portfolios } = await this.getAllClientPortfolios();
      
      // Calculate aggregated metrics
      const totalAUM = portfolios.reduce((sum, p) => sum + (p.total_value || 0), 0);
      const averageHealth = portfolios.reduce((sum, p, _, arr) => 
        sum + (p.health_score || 0) / arr.length, 0);
      const needsAttention = portfolios.filter(p => p.status === 'attention').length;
      const needsRebalancing = portfolios.filter(p => p.status === 'rebalance').length;

      return {
        portfolios,
        summary: {
          totalAUM,
          averageHealth,
          needsAttention,
          needsRebalancing,
          totalClients: portfolios.length
        }
      };
    } catch (error) {
      console.error('Error fetching global dashboard data:', error);
      throw error;
    }
  }

  /**
   * Simulate portfolio changes
   */
  async simulatePortfolio(clientId: string, transactions: Array<{
    type: 'buy' | 'sell' | 'add_cash';
    symbol?: string;
    quantity?: number;
    price?: number;
    amount?: number;
    currency?: string;
  }>, portfolioId?: string) {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.post(`/api/portfolio/simulate/${clientId}${query}`, { transactions });
  }

  /**
   * Save portfolio proposal
   */
  async saveProposal(clientId: string, proposalData: {
    name: string;
    type?: string;
    transactions?: Array<{
      type: 'buy' | 'sell' | 'add_cash';
      symbol?: string;
      quantity?: number;
      price?: number;
      amount?: number;
      currency?: string;
    }>;
    current_portfolio?: Record<string, unknown>;
    proposed_portfolio?: Record<string, unknown>;
    rationale?: string;
    advisor_notes?: string;
    current_metrics?: Record<string, unknown>;
    proposed_metrics?: Record<string, unknown>;
    total_commission?: number;
    total_stamp_duty?: number;
    total_fees?: number;
  }) {
    return httpClient.post(`/api/portfolio/proposals/${clientId}`, proposalData);
  }

  /**
   * Get portfolio proposals
   */
  async getProposals(clientId: string, status?: string) {
    const params = status ? `?status=${status}` : '';
    return httpClient.get(`/api/portfolio/proposals/${clientId}${params}`);
  }

  /**
   * Update proposal status
   */
  async updateProposalStatus(proposalId: string, status: string, feedback?: string) {
    return httpClient.put(`/api/portfolio/proposals/${proposalId}/status`, { status, feedback });
  }

  // ============= Drift Calculation (TASK-277) =============

  /**
   * Get portfolio drift from target allocation.
   * TASK-277: Drift is calculated vs risk_profile.section7.
   * Returns 422 error if client has no risk profile.
   */
  async getDrift(clientId: string, portfolioId?: string): Promise<{
    client_id: string;
    portfolio_id: string | null;
    drift: {
      // TASK-723 canonical contract — the ONE drift classification. Both the Radar signal
      // (`portfolio_drift_breach`) and Composition read this; neither derives a verdict of
      // its own any more.
      /** 'assessed' — a real classification. 'incomplete' — it could NOT be assessed. */
      assessment_status?: 'assessed' | 'incomplete';
      assessed_at?: string;
      /** Bumped when the classification changes in a way that alters verdicts. */
      calculation_version?: number;
      /** What would have to exist for an 'incomplete' assessment to become possible. */
      missing_prerequisites?: string[];
      /** The breached sub-asset-class keys. `needs_rebalancing` is derived from this. */
      breached_slots?: string[] | null;
      /** TASK-875: the labelled figure. Show this, not `drift_percentage`. Null when the
       *  assessment could not be made — never a zero that would read as compliant. */
      headline?: DriftHeadline | null;
      /** Whole-portfolio dispersion: the sum of |drift| across all 12 slots, halved. NOT
       *  comparable to `tolerance_pct`, which applies per slot. Persisted to the drift
       *  history, which is why it is still here. */
      drift_percentage: number;
      // ±band (pp) the breach flags were computed against — the CLIENT's own section-7
      // tolerance, never a constant.
      tolerance_pct?: number;
      current_allocation: Record<string, number>;
      target_allocation: Record<string, number>;
      drift_by_asset_class: Record<string, {
        current: number;
        target: number;
        drift: number;
        drift_value: number;
        status: string;
        /** True when |drift| exceeds the client's tolerance band. */
        breach?: boolean;
        asset_class?: string | null;
        sub_asset_class?: string | null;
      }>;
      /** null when the assessment is incomplete — NEVER read null as "no rebalancing needed". */
      needs_rebalancing: boolean | null;
      rebalance_urgency: string;
      suggested_trades: Array<{
        asset_class: string;
        action: 'BUY' | 'SELL';
        amount: number;
        amount_percentage: number;
        reason: string;
        priority: string;
      }>;
      total_portfolio_value: number;
      risk_profile: string | null;
      strategy: string | null;
    };
    timestamp: string;
  }> {
    const query = this.buildQueryParams({ portfolio_id: portfolioId });
    return httpClient.get(`/api/portfolio/drift/${clientId}${query}`);
  }

  // ============= My Book Aggregation (TASK-277) =============

  /**
   * Get My Book summary - aggregated view of all clients.
   * TASK-277: Advisor-level dashboard.
   */
  async getMyBookSummary(
    benchmark: string = 'SPI',
    // TASK-495: server-honoured book filters (Tier A). status (CSV) + AUM range are
    // applied by /my-book/summary; absent fields are no-ops.
    serverFilters?: { status?: string[]; minAum?: number; maxAum?: number },
  ): Promise<{
    summary: {
      total_aum: number;
      total_clients: number;
      clients_healthy: number;
      clients_needing_attention: number;
      clients_needing_rebalancing: number;
      clients_no_risk_profile: number;
      average_portfolio_size: number;
      book_performance_ytd: number | null;
      book_performance_mtd: number | null;
      benchmark_ytd: number | null;
      alpha: number | null;
      benchmark_code: string;
      benchmark_name: string;
    };
    clients: Array<{
      client_id: string;
      client_name: string;
      client_type: string;
      total_value: number;
      risk_profile: string | null;
      drift_status: 'on_target' | 'rebalance_needed' | 'no_risk_profile';
      /** TASK-875: show this. The row's status comes from `breached_slots`, and this is the
       *  figure that justifies it — `drift_percentage` is not. */
      headline?: DriftHeadline | null;
      drift_percentage: number | null;
      last_meeting: string | null;
      status: 'healthy' | 'attention' | 'rebalance';
    }>;
    timestamp: string;
  }> {
    const params = new URLSearchParams({ benchmark });
    if (serverFilters?.status?.length) params.set('status', serverFilters.status.join(','));
    if (serverFilters?.minAum != null) params.set('min_aum', String(serverFilters.minAum));
    if (serverFilters?.maxAum != null) params.set('max_aum', String(serverFilters.maxAum));
    return httpClient.get(`/api/portfolio/my-book/summary?${params.toString()}`);
  }

  /**
   * Get My Book alerts - clients needing attention.
   * TASK-277: Actionable alerts for advisor.
   */
  async getMyBookAlerts(): Promise<{
    alerts: Array<{
      type: 'missing_risk_profile' | 'rebalancing_needed';
      severity: 'high' | 'medium' | 'low';
      client_id: string;
      client_name: string;
      message: string;
      action: string;
      drift_percentage?: number;
      suggested_trades?: Array<{
        asset_class: string;
        action: string;
        amount: number;
      }>;
    }>;
    total_alerts: number;
    high_priority: number;
    timestamp: string;
  }> {
    return httpClient.get('/api/portfolio/my-book/alerts');
  }

  /**
   * Get top holdings across all portfolios.
   * TASK-277: Concentration risk analysis.
   */
  async getMyBookTopHoldings(limit = 10): Promise<{
    top_holdings: Array<{
      isin: string;
      security_name: string;
      asset_class: string;
      sector: string | null;
      currency: string;
      portfolio_count: number;
      total_value: number;
      total_quantity: number;
      client_names: string[];
    }>;
    count: number;
    timestamp: string;
  }> {
    return httpClient.get(`/api/portfolio/my-book/top-holdings?limit=${limit}`);
  }

  // ============= Portfolio Management Methods (TASK-263) =============

  /**
   * Get all portfolios for a client
   * TASK-263: Multi-portfolio support
   */
  async getClientPortfolios(clientId: string, includeInactive = false): Promise<{
    portfolios: Portfolio[];
    count: number;
  }> {
    const query = includeInactive ? '?include_inactive=true' : '';
    return httpClient.get<{ portfolios: Portfolio[]; count: number }>(
      `/api/portfolio/clients/${clientId}/portfolios${query}`
    );
  }

  /**
   * Get a single portfolio by ID
   * TASK-263: Multi-portfolio support
   */
  async getPortfolio(portfolioId: string): Promise<{ portfolio: Portfolio }> {
    return httpClient.get<{ portfolio: Portfolio }>(`/api/portfolio/portfolios/${portfolioId}`);
  }

  /**
   * Get the default portfolio for a client
   * TASK-263: Multi-portfolio support
   */
  async getDefaultPortfolio(clientId: string): Promise<{ portfolio: Portfolio }> {
    return httpClient.get<{ portfolio: Portfolio }>(
      `/api/portfolio/clients/${clientId}/portfolios/default`
    );
  }

  /**
   * Create a new portfolio for a client
   * TASK-263: Multi-portfolio support
   */
  async createPortfolio(clientId: string, data: CreatePortfolioRequest): Promise<{
    portfolio_id: string;
    client_id: string;
    name: string;
    status: string;
  }> {
    return httpClient.post<{
      portfolio_id: string;
      client_id: string;
      name: string;
      status: string;
    }>(`/api/portfolio/clients/${clientId}/portfolios`, data);
  }

  /**
   * Update a portfolio
   * TASK-263: Multi-portfolio support
   */
  async updatePortfolio(portfolioId: string, data: Partial<CreatePortfolioRequest>): Promise<{
    portfolio_id: string;
    status: string;
  }> {
    return httpClient.put<{ portfolio_id: string; status: string }>(
      `/api/portfolio/portfolios/${portfolioId}`,
      data
    );
  }

  /**
   * Delete (soft-delete) a portfolio
   * TASK-263: Multi-portfolio support
   */
  async deletePortfolio(portfolioId: string): Promise<{
    portfolio_id: string;
    status: string;
  }> {
    return httpClient.delete<{ portfolio_id: string; status: string }>(
      `/api/portfolio/portfolios/${portfolioId}`
    );
  }

  /**
   * Sync portfolio data from Pretim (CSV test data)
   * TASK-277: Pretim integration
   */
  async syncPretim(clientId: string, portfolioId: string, externalPortfolioId?: string): Promise<{
    status: string;
    synced: {
      positions_synced: number;
      positions_skipped: number;
      cash_synced: number;
      transactions_synced: number;
      last_sync: string;
    };
    errors: string[] | null;
  }> {
    return httpClientLongRunning.post(PRETIM_ENDPOINTS.SYNC, {
      client_id: clientId,
      portfolio_id: portfolioId,
      external_portfolio_id: externalPortfolioId
    });
  }

  /**
   * Check Pretim integration status
   * TASK-321: Updated to return API connectivity info
   */
  async getPretimStatus(): Promise<{
    mode: string;
    api_configured: boolean;
    api_reachable?: boolean;
    api_response_time_ms?: number;
    message: string;
  }> {
    return httpClient.get(PRETIM_ENDPOINTS.STATUS);
  }

  // ============= Sync Management (TASK-321) =============

  /**
   * Get sync history for a portfolio
   * TASK-321: Returns list of sync log entries
   */
  async getSyncHistory(
    portfolioId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<SyncHistoryResponse> {
    return httpClient.get<SyncHistoryResponse>(
      `${PRETIM_ENDPOINTS.SYNC_HISTORY(portfolioId)}?limit=${limit}&offset=${offset}`
    );
  }

  /**
   * Get portfolio with sync configuration
   * TASK-321: Returns portfolio details including sync fields
   */
  async getPortfolioSyncInfo(portfolioId: string): Promise<PortfolioWithSyncInfo> {
    return httpClient.get<PortfolioWithSyncInfo>(
      PRETIM_ENDPOINTS.PORTFOLIO(portfolioId)
    );
  }

  /**
   * Update portfolio sync configuration
   * TASK-321: Configure data source and sync settings
   */
  async updateSyncConfig(
    portfolioId: string,
    config: SyncConfigUpdate
  ): Promise<{ portfolio_id: string; status: string }> {
    return httpClient.put<{ portfolio_id: string; status: string }>(
      PRETIM_ENDPOINTS.PORTFOLIO_CONFIG(portfolioId),
      config
    );
  }

  /**
   * TASK-407: trigger an LLM-generated portfolio PPTX export.
   * Thin wrapper over `exportApi.getOrCreatePortfolioExport` — kept here so
   * the portfolio surface can import a single module for both portfolio data
   * and portfolio export. Throws `MultiplePortfoliosError` from exportApi
   * when the client has >1 portfolio and `portfolio_id` was not provided.
   */
  async exportPortfolioPptx(
    clientId: string,
    options: import('./exportApi').PortfolioExportOptions = {}
  ): Promise<import('./exportApi').ExportResponse> {
    const { getOrCreatePortfolioExport } = await import('./exportApi');
    return getOrCreatePortfolioExport(clientId, options);
  }
}

// Export singleton instance
export const portfolioApiService = new PortfolioApiService();